/**
 * useWebSEO Hook
 * Sets document title, meta description, keywords, Open Graph tags,
 * canonical URL, og:image, and JSON-LD structured data on web.
 * No-op on native platforms.
 */

import { useEffect } from 'react';
import { Platform } from 'react-native';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  ogImage?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMetaTag(name: string, content: string) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonicalLink(url: string) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

const JSON_LD_SCRIPT_ID = 'wealfi-json-ld';

function setJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  if (typeof document === 'undefined') return;
  let script = document.getElementById(JSON_LD_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = JSON_LD_SCRIPT_ID;
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeJsonLd() {
  if (typeof document === 'undefined') return;
  const script = document.getElementById(JSON_LD_SCRIPT_ID);
  if (script) {
    script.remove();
  }
}

export function useWebSEO(config: SEOConfig) {
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    document.title = config.title;
    setMetaTag('description', config.description);

    if (config.keywords) {
      setMetaTag('keywords', config.keywords);
    }

    // Canonical URL
    if (config.canonical) {
      setCanonicalLink(config.canonical);
    }

    // Open Graph
    setMetaProperty('og:title', config.ogTitle || config.title);
    setMetaProperty('og:description', config.ogDescription || config.description);
    setMetaProperty('og:type', config.ogType || 'website');
    if (config.ogUrl) {
      setMetaProperty('og:url', config.ogUrl);
    }
    if (config.ogImage) {
      setMetaProperty('og:image', config.ogImage);
    }

    // Twitter Card
    setMetaTag('twitter:card', config.ogImage ? 'summary_large_image' : 'summary');
    setMetaTag('twitter:title', config.ogTitle || config.title);
    setMetaTag('twitter:description', config.ogDescription || config.description);
    if (config.ogImage) {
      setMetaTag('twitter:image', config.ogImage);
    }

    // JSON-LD structured data
    if (config.jsonLd) {
      setJsonLd(config.jsonLd);
    }

    return () => {
      removeJsonLd();
    };
  }, [
    config.title,
    config.description,
    config.keywords,
    config.ogTitle,
    config.ogDescription,
    config.ogUrl,
    config.ogType,
    config.ogImage,
    config.canonical,
    config.jsonLd,
  ]);
}

export type { SEOConfig };
export default useWebSEO;
